import React, { useState, useEffect, useRef, useContext } from "react";
import * as bootstrap from "bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import useEvacuationStore from "../../stores/evacuation-store.js";
import useAuthStore from "../../stores/auth-store.js";
import {
  useActiveEvent,
  useCreateEvacuationOrder,
  useEvacuationZonesForEvent,
} from "../../hooks/useEvacuation.js";
import { AppSettings } from "../../config/app-settings.js";
import Swal from "sweetalert2";

const EvacuationPortalDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(AppSettings);

  // ==================== ZUSTAND STORE ====================
  const featureRequest = useEvacuationStore((state) => state.featureRequest);
  const setLoadingOrder = useEvacuationStore((state) => state.setLoadingOrder);
  const addDynamicLayer = useEvacuationStore((state) => state.addDynamicLayer);
  const setCurrentOrder = useEvacuationStore((state) => state.setCurrentOrder);
  const replaceDynamicLayers = useEvacuationStore(
    (state) => state.replaceDynamicLayers
  );
  const selectionContext = useEvacuationStore(
    (state) => state.selectionContext
  );
  const currentOrder = useEvacuationStore((state) => state.currentOrder);
  const layerRegistry = useEvacuationStore((state) => state.layerRegistry);
  const visibleLayers = useEvacuationStore((state) => state.visibleLayers);
  const user = useAuthStore((state) => state.user);

  // ==================== REACT QUERY HOOKS ====================
  const { data: eventData, isLoading: isLoadingEvent, isFetching: isFetchingEvent } = useActiveEvent(
    user?.id
  );
  const activeEventId = eventData?.ID;
  const { mutateAsync: createOrder, isLoading: isCreatingOrder } =
    useCreateEvacuationOrder();

  // Fetch zones for dropdown (only on report page)
  const pathName = location.pathname.split("/")[2];
  const isReportPage = pathName === "evacuation-order-report";
  const params = new URLSearchParams(location.search);
  const requestedEventId = params.get("eventId");
  console.log(activeEventId, requestedEventId);

  // Priority order for effective event ID
  // 1. requestedEventId (from URL) - highest priority
  // 2. activeEventId (current active event) - fallback
  // 3. null (triggers warning) - last resort
  const effectiveEventId = isReportPage
    ? requestedEventId || activeEventId
    : activeEventId;
  console.log(effectiveEventId);

  const { data: zonesData, isLoading: loadingZones } =
    useEvacuationZonesForEvent(effectiveEventId);

  // ==================== LOCAL STATE ====================
  const [currentProperties, setCurrentProperties] = useState({});
  const [currentlyInZone, setCurrentlyInZone] = useState(100);
  const [effectiveLanes, setEffectiveLanes] = useState(1.0);
  const [tcpEnabled, setTcpEnabled] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [avoidRecalculation, setAvoidRecalculation] = useState(false);
  const [displayCurrentlyInZone, setDisplayCurrentlyInZone] = useState(100);
  const [displayEffectiveLanes, setDisplayEffectiveLanes] = useState(1.0);
  const [dropdownZoneId, setDropdownZoneId] = useState(null);
  const [data, setData] = useState({
    title: "Evacuation Order",
    isFeatureSelected: false,
    zoneNumber: "No Zone Selected",
    evacuationOrderId: "-",
  });

  // ==================== REFS ====================
  const calculatedZoneRef = useRef(null);

  // ==================== LOCAL VARIABLES ====================
  const integerFields = [
    "HECTARES",
    "totalDwellings",
    "totalPeople",
    "totalVehicles",
    "currentPeople",
    "currentVehicles",
  ];
  const idFields = ["CSDUID", "ZONE_NUMB", "TAZ", "OBJECTID", "ZONE_ID"];
  const isCommitted = data.rawFeature?.order_details?.Is_Committed;
  const isRescinded = data.rawFeature?.order_details?.Is_Rescinded;
  const config = {
    flexDirection: isReportPage ? "flex-row" : "flex-column",
    colSizeFirst: isReportPage ? "col-xxl-2" : "col-lg-12",
    colSizeSecond: isReportPage ? "col-xxl-6" : "col-lg-12",
    colSizeThird: isReportPage ? "col-xxl-4" : "col-lg-12",
    colButton: isReportPage ? "col-xxl-6" : "col-lg-12",
    showCloseButton: !isReportPage,
    showForm: isReportPage,
    sliderDirection: isReportPage ? "flex-row" : "flex-column",
  };

  // ==================== FUNCTIONS ====================
  const formatValue = (key, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || !isFinite(num)) return String(value);

    if (integerFields.includes(key)) {
      if (idFields.includes(key)) {
        return Math.round(num).toString();
      } else {
        return Math.round(num).toLocaleString("en-US");
      }
    }

    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const normalizedDateString = dateString.replace(" ", "T");
    const date = new Date(normalizedDateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSetData = (properties) => {
    if (properties && typeof properties === "object") {
      // Calculate demographics
      let totalDwellings = 0,
        totalPeople = 0,
        totalVehicles = 0;

      properties.addresses?.forEach((address) => {
        totalDwellings += parseFloat(address.Occupied_Dwell || 0);
        totalPeople += parseFloat(address.People || 0);
        totalVehicles += parseFloat(address.Evac_Veh || 0);
      });

      totalDwellings = Math.round(totalDwellings);
      totalPeople = Math.round(totalPeople);
      totalVehicles = Math.round(totalVehicles);

      const effectiveSingleLaneQueueLength = (
        (totalVehicles * 7) /
        1000
      ).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Determine status
      let status = "DRAFT";
      if (properties.order_details?.Is_Alerted) {
        status = "ALERTED";
      }
      if (properties.order_details?.Is_Committed) {
        status = "ACTIVE";
        if (properties.order_details?.Is_Rescinded) {
          status = "RESCINDED";
        }
      }

      // Get calculation data
      let google_api_time_seconds,
        google_api_distance_meters,
        firstExitTime,
        delayMedianTime,
        delayLastTime;

      // Use committed data if available
      if (
        properties.order_details?.Is_Committed ||
        properties.order_details?.Is_Rescinded
      ) {
        console.log("Using committed/rescinded order data for calculations");
        // Use committed order data
        google_api_time_seconds = parseFloat(
          properties.order_details?.Google_API_Duration_Traffic_Seconds || 0
        );
        google_api_distance_meters = parseFloat(
          properties.order_details?.Google_API_Distance_Meters || 0
        );
        firstExitTime = Math.round(
          properties.order_details?.Time_First_Minutes || 0
        );
        delayMedianTime = Math.round(
          properties.order_details?.Median_Car_Exit_Delay || 0
        );
        delayLastTime = Math.round(
          properties.order_details?.Last_Car_Exit_Delay || 0
        );

        // Sync UI controls with committed values
        setAvoidRecalculation(true);
        setCurrentlyInZone(properties.order_details?.Currently_In_Zone || 100);
        setTcpEnabled(properties.order_details?.Traffic_Control || false);
        setEffectiveLanes(properties.order_details?.Effective_Lanes || 1.0);
        setDisplayCurrentlyInZone(
          properties.order_details?.Currently_In_Zone || 100
        );
        setDisplayEffectiveLanes(
          properties.order_details?.Effective_Lanes || 1.0
        );
      }

      // Use calculated data
      else {
        console.log("Using calculated order data for calculations");
        // Use calculated data
        google_api_time_seconds = parseFloat(
          properties.calculations
            ?.google_api_current_duration_in_traffic_seconds || 0
        );
        google_api_distance_meters = parseFloat(
          properties.calculations?.google_api_current_distance_meters || 0
        );
        firstExitTime = Math.round(
          properties.calculations?.time_first_minutes || 0
        );
        delayMedianTime = Math.round(
          properties.calculations?.median_car_area_exit_delay_time || 0
        );
        delayLastTime = Math.round(
          properties.calculations?.last_car_area_exit_delay_time || 0
        );
      }

      const threshold = 60; // minutes
      const total_travel_time_median = firstExitTime + delayMedianTime;
      const total_travel_time_last = firstExitTime + delayLastTime;

      // Build data object
      const processedData = {
        // Event details
        eventTitle: properties.order_details?.Event_Title || "-",
        eventStartDate: properties.order_details?.Event_Start_Date || "-",
        eventStartTime: properties.order_details?.Event_Start_Time || "-",
        eventStatus: properties.order_details?.Event_Status || "-",
        eventEndDate: properties.order_details?.Event_End_Date || "-",
        eventEndTime: properties.order_details?.Event_End_Time || "-",

        status: status,
        title: `Evacuation Order - Zone ${
          properties.evacuation_data?.O_Zone || "Unknown"
        }`,
        isFeatureSelected: true,
        zoneNumber: properties.evacuation_data?.O_Zone || "Unknown",
        evacuationOrderId: properties.order_details?.ID || "Processing...",

        // Demographics
        totalDwellings: formatValue("totalDwellings", totalDwellings),
        totalPeople: formatValue("totalPeople", totalPeople),
        totalVehicles: formatValue("totalVehicles", totalVehicles),
        rawTotalPeople: totalPeople,
        rawTotalVehicles: totalVehicles,
        currentPeople: formatValue(
          "CURRENT_PEOPLE",
          Math.round(totalPeople * 1)
        ),
        currentVehicles: formatValue(
          "CURRENT_VEHICLES",
          Math.round(totalVehicles * 1)
        ),
        effectiveSingleLaneQueueLength: effectiveSingleLaneQueueLength,

        // Destination
        musterStation: properties.evacuation_data?.Dest_Name || "-",
        address: properties.evacuation_data?.Dest_Address || "-",
        city: properties.evacuation_data?.Dest_Community || "-",

        // Route
        routeDistance:
          google_api_distance_meters !== undefined
            ? `${(google_api_distance_meters / 1000).toFixed(1)}`
            : "Calculating...",
        travelTime:
          google_api_time_seconds !== undefined
            ? Math.round(google_api_time_seconds / 60)
            : "Calculating...",
        google_api_call_timestamp:
          properties.evacuation_data?.google_api_call_timestamp || 0,

        // Delays
        medianCar: delayMedianTime,
        lastCar: delayLastTime,
        medianIsWarning:
          delayMedianTime !== null && delayMedianTime > threshold,
        lastCarIsWarning: delayLastTime !== null && delayLastTime > threshold,
        threshold: threshold,
        totalTravelTimeMedian: total_travel_time_median,
        totalTravelTimeLast: total_travel_time_last,

        // Metadata
        totalAddresses: properties.addresses?.length || 0,
        rawFeature: properties,
      };

      setData(processedData);
    } else {
      // Reset to empty state
      setData({
        title: "Evacuation Order",
        status: "-",
        isFeatureSelected: false,
        zoneNumber: "-",
        evacuationOrderId: "-",
        totalDwellings: "-",
        totalPeople: "-",
        totalVehicles: "-",
        effectiveSingleLaneQueueLength: "-",
        currentPeople: "-",
        currentVehicles: "-",
        musterStation: "-",
        address: "-",
        routeDistance: "-",
        travelTime: "-",
        totalAddresses: "-",
        medianCar: "-",
        lastCar: "-",
      });
    }
  };

  const handleCreateOrder = async (
    eventId,
    zoneId,
    hasBaseParameters = false,
    percentageRemaining = 100,
    tcpEnabled = false,
    effectiveLanes = 1.0
  ) => {
    try {
      setLoadingOrder(true);

      const orderData = await createOrder({
        userId: user?.id,
        eventId: eventId,
        zoneId,
        hasBaseParameters,
        percentageRemaining,
        tcpEnabled,
        effectiveLanes,
      });

      if (isReportPage) {
        const routeGeoJson =
          orderData.evacuation_data.google_api_current_route_geojson;
        const allRoutesGeoJson =
          orderData.evacuation_data.google_api_all_route_geojson || [];
        const orderGeoJson = orderData.order_geojson;
        const newDynamicLayers = {};

        if (orderGeoJson) {
          newDynamicLayers[zoneId] = {
            data: orderGeoJson,
            linkedTo: zoneId,
            clearOn: "manual",
            timestamp: Date.now(),
          };
        }

        if (routeGeoJson) {
          // Primary route uses 'gmap_route' style name
          newDynamicLayers[`gmap_route`] = {
            data: routeGeoJson,
            linkedTo: zoneId,
            clearOn: "manual",
            timestamp: Date.now(),
          };
        }

        // Add alternative routes (if available)
        if (allRoutesGeoJson && allRoutesGeoJson.length > 1) {
          allRoutesGeoJson.forEach((altRoute, idx) => {
            if (idx > 0) {  // Skip first route (it's the primary)
              // Alternative routes use 'gmap_route_alternative' style name with index
              newDynamicLayers[`gmap_route_alternative_${idx}`] = {
                data: altRoute,
                linkedTo: zoneId,
                clearOn: "manual",
                timestamp: Date.now(),
              };
            }
          });
        }

        replaceDynamicLayers(newDynamicLayers);
        console.log(
          "✅ Dynamic layers (route, alternatives, and order geojson) replaced for report page"
        );
      }

      if (!isReportPage) {
        // Add primary route with 'gmap_route' style name
        addDynamicLayer(
          `gmap_route`,
          orderData.evacuation_data.google_api_current_route_geojson,
          {
            linkedTo: zoneId,
            clearOn: "deselect",
          }
        );
        
        // Add alternative routes if available
        const allRoutesGeoJson =
          orderData.evacuation_data.google_api_all_route_geojson || [];
        if (allRoutesGeoJson && allRoutesGeoJson.length > 1) {
          allRoutesGeoJson.forEach((altRoute, idx) => {
            if (idx > 0) {  // Skip first route (it's the primary)
              // Alternative routes use 'gmap_route_alternative' style name with index
              addDynamicLayer(
                `gmap_route_alternative_${idx}`,
                altRoute,
                {
                  linkedTo: zoneId,
                  clearOn: "deselect",
                }
              );
            }
          });
          console.log(
            `✅ Dynamic layers (primary route + ${allRoutesGeoJson.length - 1} alternatives) added to layerRegistry store for portal page`
          );
        } else {
          console.log(
            "✅ Dynamic layers (only primary route) added to layerRegistry store for portal page"
          );
        }

        const musterLat = orderData.evacuation_data?.Dest_Lat;
        const musterLon = orderData.evacuation_data?.Dest_Lon;
        const musterName = orderData.evacuation_data?.Dest_Name?.trim();
        const musterAddress = orderData.evacuation_data?.Dest_Address?.trim();
        if (musterLat && musterLon) {
          useEvacuationStore.getState().updateSelectionContext({
            musterCoordinates: { lat: musterLat, lon: musterLon },
            musterName: musterName,
            musterAddress: musterAddress,
          });
        }
        console.log(
          "✅ Muster station info added to selectionContext store for portal page"
        );
      }

      setCurrentOrder({
        addresses: orderData.addresses || [],
        order_details: {
          ID: orderData.order_details?.ID || null,
          Is_Alerted: orderData.order_details?.Is_Alerted || false,
          Is_Committed: orderData.order_details?.Is_Committed || false,
          Is_Rescinded: orderData.order_details?.Is_Rescinded || false,
          Zone_ID: orderData.order_details?.Evacuation_Zone_ID || null,
          Commit_Order_Date: orderData.order_details?.Commit_Order_Date || null,
          Commit_Order_Time: orderData.order_details?.Commit_Order_Time || null,
          Rescind_Order_Date:
            orderData.order_details?.Rescind_Order_Date || null,
          Rescind_Order_Time:
            orderData.order_details?.Rescind_Order_Time || null,
        },
        calculations: orderData.calculations || {},
        evacuation_data: orderData.evacuation_data || {},
      });

      console.log("✅ Order added to currentOrder store for portal page");

      return orderData;
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleZoneChange = (newZoneId) => {
    if (!newZoneId || !effectiveEventId) {
      console.warn("⚠️ Invalid zone selection or missing event ID");
      return;
    }

    console.log("🎯 User selected zone from dropdown:", newZoneId);

    // Reset calculation ref to allow new calculation
    calculatedZoneRef.current = null;

    // Update URL with new zone (preserve eventId)
    const newSearchParams = new URLSearchParams({
      eventId: effectiveEventId,
      zoneId: newZoneId,
    });

    navigate(`?${newSearchParams.toString()}`, { replace: true });

    console.log("✅ URL updated and zone selection synced");
  };

  const handleNoEventIdAlert = () => {
    Swal.fire({
      icon: "info",
      title: "No Active or Selected Event",
      text: "There is currently no active or selected evacuation event for your account. You will be redirected to the Evacuation Events page.",
      confirmButtonText: "OK",
    }).then(() => {
      navigate("/evacuation-portal-panels/evacuation-events/", {
        replace: true,
      });
    });
  };

  // ==================== EFFECTS ====================
  // Popover
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );
    const popoverInstances = [];

    if (popoverTriggerList.length > 0) {
      popoverTriggerList.forEach((elm) => {
        const popover = new bootstrap.Popover(elm);
        popoverInstances.push(popover);
      });
    }

    // Cleanup popovers on unmount
    return () => {
      popoverInstances.forEach((popover) => {
        try {
          popover.dispose();
        } catch (error) {
          console.warn("Error disposing popover:", error);
        }
      });
    };
  }, []);

  // Data fetch for dropdown zones (report page) and zone status coloring (portal page map)
  useEffect(() => {
    if (zonesData) {
      useEvacuationStore.getState().setZonesData(zonesData);

      console.log("✅ Zones data stored in Zustand");
    }
  }, [zonesData]);

  // Keep the map's loading state up until zone status data has arrived
  useEffect(() => {
    useEvacuationStore.getState().setLoadingZoneStatuses(loadingZones);
  }, [loadingZones]);

  // Sync dropdown with URL parameter
  useEffect(() => {
    console.log("🔍 Current dropdownZoneId:", dropdownZoneId);
    if (!isReportPage) return; // Only on report page

    const params = new URLSearchParams(location.search);
    const urlZoneId = params.get("zoneId");

    // Sync dropdown selection with URL
    if (urlZoneId && urlZoneId !== dropdownZoneId) {
      console.log("🔄 Syncing dropdown with URL parameter:", urlZoneId);
      setDropdownZoneId(urlZoneId);
    }
  }, [location.search, isReportPage]);

  // Data fetch on feature request change
  useEffect(() => {
    const handleCalculate = async (
      eventId = null,
      zoneId = null,
      hasBaseParameters = false,
      percentageRemaining = 100,
      tcpEnabled = false,
      effectiveLanes = 1.0
    ) => {
      if (!zoneId || !eventId) {
        console.warn("⚠️ Missing zoneId or eventId");
        return;
      }

      // Prevent duplicate calls for same zone
      if (calculatedZoneRef.current === zoneId) {
        console.log("🔄 Zone already calculated, skipping...");
        return;
      }

      try {
        // Mark as calculating (only set flag for initial calculation)
        calculatedZoneRef.current = zoneId;

        setIsRecalculating(hasBaseParameters); // Show recalculation spinner if it is the case
        setLoadingOrder(true);

        console.log("🔄 Fetching order data:", {
          eventId,
          zoneId,
          hasBaseParameters,
          percentageRemaining,
          tcpEnabled,
          effectiveLanes,
        });

        // Single API call for both initial calculation and recalculation
        const order = await handleCreateOrder(
          eventId,
          zoneId,
          hasBaseParameters,
          percentageRemaining,
          tcpEnabled,
          effectiveLanes
        );

        // Sync UI with API response
        const apiPercentage = order.calculations?.percentage_remaining || 100;
        const apiTcpEnabled = order.calculations?.tcp_enabled || false;
        const apiEffectiveLanes = order.calculations?.effective_lanes || 1.0;

        setAvoidRecalculation(true);
        setCurrentlyInZone(apiPercentage);
        setTcpEnabled(apiTcpEnabled);
        setEffectiveLanes(apiEffectiveLanes);
        setDisplayCurrentlyInZone(apiPercentage);
        setDisplayEffectiveLanes(apiEffectiveLanes);

        // Build enhanced properties
        const properties = {
          zoneId: zoneId,
          addresses: order.addresses || [],
          order_details: order.order_details || {},
          calculations: order.calculations || {},
          evacuation_data: order.evacuation_data || {},
          order_geojson: order.order_geojson || null,
        };

        setCurrentProperties(properties);
        handleSetData(properties);

        console.log("✅ Order data processed successfully");
      } catch (error) {
        console.error("❌ Error calculating order:", error);
      } finally {
        setLoadingOrder(false);
        setIsRecalculating(false);
      }
    };

    // No feature selected - reset UI
    if (
      !featureRequest ||
      Object.keys(featureRequest).length === 0 ||
      !featureRequest.zoneId
    ) {
      console.log(
        "⏸️ No valid featureRequest or missing zoneId, skipping fetch"
      );
      handleSetData(null);
      calculatedZoneRef.current = null;
      return;
    }

    console.log("Active Event ID:", activeEventId);
    const zoneId = featureRequest?.zoneId;
    const eventId = featureRequest?.eventId || activeEventId;
    const params = featureRequest?.params || {};

    // Reset calculation ref if zone changed via dropdown
    if (dropdownZoneId && dropdownZoneId !== calculatedZoneRef.current) {
      calculatedZoneRef.current = null;
    }

    // Determine if this is initial calculation or recalculation
    const hasCustomParams = Object.keys(params).length > 0;

    // RECALCULATION, PROCESS button or URL Navigation with custom parameters
    if (hasCustomParams) {
      const percentageRemaining = params.percentageRemaining || 100;
      const tcpEnabled = params.tcpEnabled || false;
      const effectiveLanes = params.effectiveLanes || 1.0;

      console.log("🔄 Triggering calculation with custom parameters");
      handleCalculate(
        eventId,
        zoneId,
        true,
        percentageRemaining,
        tcpEnabled,
        effectiveLanes
      );
    }

    // INITIAL CALCULATION: map click, dropdown, or URL navigation with default parameters
    else {
      console.log("🆕 Triggering initial calculation with default parameters");
      handleCalculate(eventId, zoneId, false, 100, false, 1.0);
    }
    // activeEventId is included because a zone can be clicked on the map (portal page)
    // before useActiveEvent() resolves; without this dependency the effect never
    // re-runs once the event ID becomes available, silently skipping the fetch forever.
  }, [featureRequest, activeEventId]);

  // Trigger recalculation when sliders/toggles change
  useEffect(() => {
    /** Skip if:
     * - No zone selected
     * - Already recalculating
     * - Avoiding recalculation (during initial load)
     * - Order is committed/rescinded
     */

    if (
      !currentProperties?.zoneId ||
      avoidRecalculation ||
      isRecalculating ||
      isCommitted ||
      isRescinded
    ) {
      return;
    }

    console.log("🔄 Parameters changed, triggering recalculation...");

    calculatedZoneRef.current = null;

    // Trigger recalculation via featureRequest
    const timer = setTimeout(() => {
      useEvacuationStore.getState().setFeatureRequest({
        eventId: activeEventId,
        zoneId: currentProperties.zoneId,
        params: {
          percentageRemaining: currentlyInZone,
          tcpEnabled: tcpEnabled,
          effectiveLanes: effectiveLanes,
        },
      });
    }, 300); // Debounce to avoid excessive API calls

    return () => clearTimeout(timer);
  }, [currentlyInZone, tcpEnabled, effectiveLanes]);

  // Check for missing event ID on report page
  useEffect(() => {
    // Only check on report page
    if (!isReportPage) return;

    // ✅ Wait for active event query to finish loading OR fetching
    // isFetchingEvent is true during background refetch (e.g., client context switch)
    if (isLoadingEvent || isFetchingEvent) {
      console.log("⏳ Waiting for active event query to complete...");
      return;
    }

    // Check if we have any event ID available
    const hasEventId = requestedEventId || activeEventId;

    if (!hasEventId) {
      console.warn(
        "⚠️ Report page loaded without any event ID (no URL eventId and no active event)"
      );
      handleNoEventIdAlert();
    } else {
      console.log("✅ Event ID available:", {
        requestedEventId,
        activeEventId,
        effectiveEventId,
        source: requestedEventId ? "URL parameter" : "Active event",
      });
    }
  }, [isReportPage, activeEventId, requestedEventId, isLoadingEvent, isFetchingEvent]);

  // Mount/Unmount
  useEffect(() => {
    if (
      location.pathname.split("/")[2] !== "evacuation-portal" &&
      location.pathname.split("/")[2] !== "evacuation-order-report"
    ) {
      console.log(
        "⏸️ Not on evacuation-portal path, skipping mount log:",
        location.pathname.split("/")[2]
      );
      return;
    }

    console.log(
      "🎬 EvacuationPortalDetails mounted for:",
      isReportPage ? "REPORT" : "PORTAL"
    );

    return () => {
      console.log(
        "🧹 EvacuationPortalDetails unmounting - setting cleanup flag"
      );
    };
  }, [location.pathname]);

  // ==================== RENDER ====================
  return (
    <React.Fragment>
      {/* Header */}
      <div
        className="sticky-top p-3 d-flex justify-content-between align-items-center text-warning"
        style={{ backgroundColor: "#161a1d", zIndex: 10 }}
      >
        <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
          <b id="selectedTitle">{data.title}</b>
          <i
            className="fa fa-info-circle"
            data-bs-toggle="popover"
            data-bs-trigger="hover"
            data-bs-title="Selected Element"
            data-bs-placement="bottom"
            data-bs-content="Information about the selected element on the map."
          ></i>
        </h5>

        {/* Zone Dropdown - Report Page Only */}
        {isReportPage && (
          <div className="mb-0">
            <select
              id="select-zone"
              className="form-select form-select-sm bg-dark text-warning border-warning"
              value={dropdownZoneId || ""}
              onChange={(e) => handleZoneChange(e.target.value)}
              disabled={loadingZones || isCreatingOrder || isRecalculating}
            >
              <option value="">
                {loadingZones ? "Loading zones..." : "Select a zone..."}
              </option>
              {zonesData?.map((zone) => (
                <option key={zone.Zone_ID} value={zone.Zone_ID}>
                  Zone {zone.Zone_ID}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Close Button - Portal Page Only */}
        {config.showCloseButton && (
          <button
            onClick={() => context.setAppSidebarEndToggled(false)}
            className="btn btn-sm btn-link p-0 text-danger"
            style={{ textDecoration: "none" }}
          >
            <i className="fa-solid fa-2x fa-circle-xmark"></i>
          </button>
        )}
      </div>

      {/* Dashboard Grid */}
      <div className="container-fluid p-4">
        <div className="row justify-content-evenly g-4">
          {/* Left Column: Event, Order, Destination */}
          <div
            className={`${config.colSizeFirst} d-flex flex-column justify-content-evenly gap-4`}
          >
            {/* Event Details - Report Page Only */}
            {isReportPage && (
              <div className="card bg-light shadow-sm">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="fa-solid fa-calendar-days text-warning"></i>
                    <h5 className="mb-0 text-warning fw-bolder">
                      Event Details
                    </h5>
                  </div>
                  <div className="mb-3">
                    <h6 className="text-gray-600 d-block mb-1">Event Title</h6>
                    <h5 className="fw-bolder text-dark">{data.eventTitle}</h5>
                  </div>
                  <div>
                    <h6 className="text-gray-600 d-block mb-1">Event Status</h6>
                    <h5
                      className={`fw-bolder ${
                        data.eventStatus === "Ended"
                          ? "text-danger"
                          : "text-dark"
                      }`}
                    >
                      {data.eventStatus}
                    </h5>
                  </div>
                </div>
              </div>
            )}

            {/* Evacuation Order */}
            <div className="card bg-light shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa-solid fa-route fa-lg text-warning"></i>
                  <h5 className="text-warning fw-bolder mb-0">
                    Evacuation Order
                  </h5>
                </div>
                <div className="mb-2 d-flex justify-content-between">
                  <h5>Order ID</h5>
                  <h5 className="fw-bolder font-monospace fs-3">
                    {data.evacuationOrderId}
                  </h5>
                </div>
                <div className="mb-2 d-flex justify-content-between">
                  <h5>Status</h5>
                  <h6 className="fw-bolder text-danger">{data.status}</h6>
                </div>
                <div className="d-flex justify-content-between">
                  <h5>Priority</h5>
                  <h6 className="fw-bolder text-danger">HIGH</h6>
                </div>
              </div>
            </div>

            {/* Destination */}
            <div className="card bg-light shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa fa-map-marker-alt fa-lg text-warning"></i>
                  <h5 className="mb-0 text-warning fw-bolder">Destination</h5>
                </div>
                <div className="mb-3">
                  <h6 className="text-gray-600 d-block mb-1">Muster Station</h6>
                  <h5 className="fw-bolder text-dark">{data.musterStation}</h5>
                </div>
                <div>
                  <h6 className="text-gray-600 d-block mb-1">Address</h6>
                  <h5 className="fw-bolder text-dark">
                    {data.address} - {data.city}
                  </h5>
                </div>
              </div>
            </div>
          </div>
          {/* Middle Column: Zone Data, Sliders */}
          <div className={`${config.colSizeSecond} d-flex flex-column gap-4`}>
            {/* Zone Data */}
            <div className="card bg-light shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa fa-database fa-lg text-warning"></i>
                  <h5 className="text-warning fw-bolder mb-0 fs-4">
                    Zone Data (
                    <span
                      className={`text-warning ms-1 ${
                        !data.isFeatureSelected ? "fst-italic text-muted" : ""
                      }`}
                    >
                      {data.isFeatureSelected && `Zone ID: ${data.zoneNumber}`}
                      {!data.isFeatureSelected && "Select a zone on the map"}
                    </span>
                    )
                  </h5>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="p-4 rounded bg-secondary bg-opacity-10 text-center">
                      <h5
                        className="text-muted d-block mb-2 fs-5"
                        style={{ minHeight: "30px" }}
                      >
                        Est. Total People
                      </h5>
                      <h5 className="fw-bolder fs-2">
                        {data.totalPeople}
                        <i className="fa-solid fa-users fs-3 text-primary ms-2"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-4 rounded bg-secondary bg-opacity-10 text-center">
                      <h5
                        className="text-muted d-block mb-2 fs-5"
                        style={{ minHeight: "30px" }}
                      >
                        Est. Total Vehicles
                      </h5>
                      <h5 className="fw-bolder fs-2">
                        {data.totalVehicles}
                        <i className="fa-solid fa-car-side fs-3 text-success ms-2"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-4 rounded bg-secondary bg-opacity-10 text-center">
                      <h5
                        className="text-muted d-block mb-2 fs-5"
                        style={{ minHeight: "30px" }}
                      >
                        Est. Total Dwellings
                      </h5>
                      <h5 className="fw-bolder fs-2">
                        {data.totalDwellings}
                        <i className="fa-solid fa-house-chimney fs-3 text-warning ms-2"></i>
                      </h5>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-4 rounded bg-secondary bg-opacity-10 text-center">
                      <h5
                        className="text-muted d-block mb-2 fs-5"
                        style={{ minHeight: "30px" }}
                      >
                        Total Addresses
                      </h5>
                      <h5 className="fw-bolder fs-2">
                        {data.totalAddresses}
                        <i className="fa-solid fa-location-dot fs-3 text-danger ms-2"></i>
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sliders */}
            <div className={`d-flex gap-4 ${config.sliderDirection}`}>
              {/* Currently in Zone */}
              <div className="flex-fill card bg-light shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 text-warning fw-bolder fs-4">
                      Est. Currently in Zone
                    </h5>
                    <span className="fw-bolder fs-5 text-primary">
                      {displayCurrentlyInZone}%
                    </span>
                  </div>
                  <input
                    type="range"
                    className="form-range custom-range-dark"
                    step="0.1"
                    min="0"
                    max="100"
                    value={displayCurrentlyInZone}
                    onChange={(e) => {
                      setDisplayCurrentlyInZone(e.target.value);
                    }}
                    onMouseUp={() => {
                      setAvoidRecalculation(false);
                      setCurrentlyInZone(displayCurrentlyInZone);
                    }}
                    onTouchEnd={() => {
                      setAvoidRecalculation(false);
                      setCurrentlyInZone(displayCurrentlyInZone);
                    }}
                    disabled={isRecalculating || isCommitted || isRescinded}
                  />
                  <div className="row g-2 mt-3 pt-3 border-top">
                    <div className="col-6 text-center">
                      <h5 className="text-muted d-block mb-1 fs-5">
                        Current People
                      </h5>
                      <h5 className="fw-bolder fs-2">
                        {data.rawTotalPeople
                          ? formatValue(
                              "currentPeople",
                              Math.round(
                                data.rawTotalPeople *
                                  (displayCurrentlyInZone / 100)
                              )
                            )
                          : data.currentPeople}
                        <i className="fa-solid fa-users fs-3 text-primary ms-2" />
                      </h5>
                    </div>
                    <div className="col-6 text-center">
                      <h5 className="text-muted d-block mb-1 fs-5">
                        Current Vehicles
                      </h5>
                      <h5 className="fw-bolder fs-2">
                        {data.rawTotalVehicles
                          ? formatValue(
                              "currentVehicles",
                              Math.round(
                                data.rawTotalVehicles *
                                  (displayCurrentlyInZone / 100)
                              )
                            )
                          : data.currentVehicles}
                        <i className="fa-solid fa-car-side fs-3 text-success ms-2" />
                      </h5>
                    </div>
                  </div>
                </div>
              </div>

              {/* Traffic Control Card */}
              <div className="flex-fill card bg-light shadow-sm">
                <div className="card-body">
                  {/* Traffic Control */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa fa-solid-bolt fa-2x text-warning"></i>
                      <h5 className="mb-0 text-warning fw-bolder fs-4">
                        Traffic Control
                      </h5>
                    </div>
                    <div className="form-check form-switch fs-5">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="tsp"
                        checked={tcpEnabled}
                        onChange={(e) => {
                          setAvoidRecalculation(false);
                          setTcpEnabled(e.target.checked);
                        }}
                        disabled={
                          isCreatingOrder ||
                          isRecalculating ||
                          isCommitted ||
                          isRescinded
                        }
                      />
                      <h5 className="text-muted d-block fs-4 m-0">
                        {tcpEnabled ? "Active" : "Inactive"}
                      </h5>
                    </div>
                  </div>

                  {/* Effective Lanes */}
                  <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                    <h5 className="mb-0 text-warning fw-bolder fs-4">
                      Effective Lanes
                    </h5>
                    <h5 className="fw-bolder fs-5 text-primary">
                      {displayEffectiveLanes}
                    </h5>
                  </div>
                  <input
                    type="range"
                    className="form-range custom-range-dark"
                    step="0.1"
                    min="1"
                    max="10"
                    value={displayEffectiveLanes}
                    onChange={(e) => setDisplayEffectiveLanes(e.target.value)}
                    onMouseUp={() => {
                      setAvoidRecalculation(false);
                      setEffectiveLanes(displayEffectiveLanes);
                    }}
                    onTouchEnd={() => setAvoidRecalculation(false)}
                    disabled={
                      isCreatingOrder ||
                      isRecalculating ||
                      isCommitted ||
                      isRescinded
                    }
                  />
                  <div className="d-flex flex-column align-items-center justify-content-between mt-3 pt-3 border-top">
                    <h5 className="mb-2 text-warning fw-bolder fs-5">
                      Effective Single Lane Queue Length
                    </h5>
                    <h5 className="fw-bolder fs-4 fw-bold mb-0">
                      {data.effectiveSingleLaneQueueLength
                        ? (
                            data.effectiveSingleLaneQueueLength *
                            (currentlyInZone / 100)
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : data.effectiveSingleLaneQueueLength}{" "}
                      km
                      <i className="fa-solid fa-car-side ms-2 text-primary"></i>
                      <i className="fa-solid fa-car-side ms-2 text-primary"></i>
                      <i className="fa-solid fa-car-side ms-2 text-primary"></i>
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Column: Travel Times & Delays */}
          <div className={`${config.colSizeThird} d-flex flex-column gap-4`}>
            {/* Current Route Travel Time & Distance */}
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa-solid fa-route fa-lg text-warning"></i>
                  <h5 className="mb-0 text-warning fw-bolder fs-4">
                    Current Route Travel Time & Distance
                  </h5>
                </div>
                <div className="row g-2 mt-2 pt-3 border-top">
                  <div className="col-6">
                    <div className="p-2 rounded bg-light text-dark text-center">
                      <h5 className="text-muted d-block mb-1 fs-6">
                        Travel Time
                      </h5>
                      <i className="fa fa-clock fs-4"></i>
                      <h5 className="text-muted fw-bolder d-block mb-1 fs-3">
                        {data.travelTime} min
                      </h5>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-2 rounded bg-light text-dark text-center">
                      <h5 className="text-muted d-block mb-1 fs-6">Distance</h5>
                      <i className="fa fa-road fs-4"></i>
                      <h5 className="text-muted fw-bolder d-block mb-1 fs-3">
                        {data.routeDistance} km
                      </h5>
                    </div>
                  </div>
                  <div className="col-12">
                    <h5 className="text-muted d-block mb-1 fs-6">
                      Data acquired at:{" "}
                      {formatDate(data.google_api_call_timestamp)}
                    </h5>
                  </div>
                </div>
              </div>
            </div>

            {/* Delay Times */}
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa fa-hourglass fa-lg text-warning"></i>
                  <h5 className="mb-0 text-warning fw-bolder fs-4">
                    Delay Times
                  </h5>
                </div>
                <div className="mb-3">
                  {/* Median Car */}
                  <div
                    className="p-2 rounded mb-2"
                    style={{
                      backgroundColor: data.medianIsWarning
                        ? "#fff3cd"
                        : "#d4edda",
                      borderLeft: `4px solid ${
                        data.medianIsWarning ? "#ffc107" : "#28a745"
                      }`,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="d-block mb-1 text-warning fs-5">
                        Median Car Exit
                      </h6>
                      <h5 className="fw-bolder text-black fs-4">
                        {data.medianCar} min
                        {data.medianIsWarning ? (
                          <i className="fa fa-exclamation-triangle text-warning ms-2" />
                        ) : (
                          <i className="fa fa-check-circle text-success ms-2" />
                        )}
                      </h5>
                    </div>
                    <h6
                      className={
                        data.medianIsWarning
                          ? "fs-5 text-warning"
                          : "fs-5 text-success"
                      }
                    >
                      {data.medianIsWarning
                        ? `⚠ Above threshold (${data.threshold} min)`
                        : `✓ Within acceptable range`}
                    </h6>
                  </div>

                  {/* Last Car */}
                  <div
                    className="p-2 rounded"
                    style={{
                      backgroundColor: data.lastCarIsWarning
                        ? "#f8d7da"
                        : "#d4edda",
                      borderLeft: `4px solid ${
                        data.lastCarIsWarning ? "#dc3545" : "#28a745"
                      }`,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="d-block mb-1 text-warning fs-5">
                        Last Car Exit
                      </h6>
                      <h5 className="fw-bolder text-black fs-4">
                        {data.lastCar} min
                        {data.lastCarIsWarning ? (
                          <i className="fa fa-exclamation-triangle text-danger ms-2" />
                        ) : (
                          <i className="fa fa-check-circle text-success ms-2" />
                        )}
                      </h5>
                    </div>
                    <h6
                      className={
                        data.lastCarIsWarning
                          ? "text-danger fs-5"
                          : "text-success fs-5"
                      }
                    >
                      {data.lastCarIsWarning
                        ? `⚠ Above threshold (${data.threshold} min)`
                        : `✓ Within acceptable range`}
                    </h6>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Travel Times */}
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa-solid fa-clock fa-lg text-warning"></i>
                  <h5 className="mb-0 text-warning fw-bolder fs-4">
                    Total Travel Times (door-to-door)
                  </h5>
                </div>
                <div className="mb-3">
                  <div className="p-2 mb-2 bg-gray-200 rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="d-block text-black-900 mb-0 fs-5">
                        Median Car
                      </h6>
                      <h5 className="text-end fw-bolder text-warning mb-0 fs-4 ps-1">
                        {data.totalTravelTimeMedian} min
                      </h5>
                    </div>
                  </div>
                  <div className="p-2 mb-2 bg-gray-200 rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="d-block text-black-900 mb-0 fs-5">
                        Last Car
                      </h6>
                      <h5 className="text-end fw-bolder text-warning mb-0 fs-4 ps-1">
                        {data.totalTravelTimeLast} min
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Process Button - Portal Page Only */}
          {!isReportPage && (
            <div className={`${config.colButton} d-flex flex-column gap-4 p-0`}>
              <button
                className="btn btn-success btn-lg w-100 mt-auto"
                onClick={() => {
                  // ✅ Build URL with GET parameters
                  const params = new URLSearchParams({
                    eventId: activeEventId,
                    zoneId: currentProperties.zoneId,
                    currentlyInZone: currentlyInZone,
                    effectiveLanes: effectiveLanes,
                    tcpEnabled: tcpEnabled.toString(), // Convert boolean to string
                  });

                  // Navigate with URL parameters (no state)
                  navigate(
                    `/evacuation-portal-panels/evacuation-order-report?${params.toString()}`
                  );
                }}
                disabled={
                  !data.isFeatureSelected || isCreatingOrder || isRecalculating
                }
              >
                PROCESS EVACUATION
                <br />
                ORDER REPORT
              </button>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default EvacuationPortalDetails;
